import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Printer, Download, Loader2, X, FileWarning } from 'lucide-react';

const parseFilename = (disposition, fallback) => {
  if (!disposition) return fallback;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return m ? decodeURIComponent(m[1]) : fallback;
};

/**
 * Authenticated Personnel Data Sheet preview with Print + Download.
 * Preview renders server-generated page IMAGES (data URLs) shown as <img> — this is
 * dependable in Chrome and never relies on the native PDF plugin/CSP. Print and
 * Download use the real PDF blob (fetched with the Authorization header; no token
 * ever appears in a URL), so both work even if a browser cannot embed a PDF.
 */
export const DataSheetViewer = ({ personnelId, name, onClose, heightClass = 'h-[72vh]', showTitle = true }) => {
  const { token } = useContext(AuthContext);
  const [pages, setPages] = useState([]);
  const [fileName, setFileName] = useState('TFS_Personnel_DataSheet.pdf');
  const [state, setState] = useState('loading'); // loading | ready | error
  const blobRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/personnel/${personnelId}/summary-preview`, { headers }),
      axios.get(`${API}/personnel/${personnelId}/summary.pdf`, { headers, responseType: 'blob' }),
    ]).then(([prev, pdf]) => {
      if (cancelled) return;
      setPages(prev.data?.pages || []);
      blobRef.current = new Blob([pdf.data], { type: 'application/pdf' });
      setFileName(parseFilename(pdf.headers?.['content-disposition'], `TFS_Personnel_DataSheet_${personnelId}.pdf`));
      setState('ready');
    }).catch((e) => {
      if (cancelled) return;
      setState('error');
      toast.error(e?.response?.status === 403 ? 'You are not authorized to view this data sheet' : 'Could not load the data sheet');
    });
    return () => { cancelled = true; };
  }, [personnelId, token]);

  const doDownload = () => {
    if (!blobRef.current) return;
    const url = URL.createObjectURL(blobRef.current);
    const a = document.createElement('a');
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const doPrint = () => {
    if (!blobRef.current) return;
    const url = URL.createObjectURL(blobRef.current);
    const w = window.open(url, '_blank');
    if (w) {
      w.addEventListener('load', () => { try { w.focus(); w.print(); } catch (_) { /* user can print manually */ } });
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else {
      URL.revokeObjectURL(url);
      toast.error('Allow pop-ups to print, or use Download.');
    }
  };

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      {showTitle ? (
        <span className="text-sm font-semibold text-[#0E2A47]" data-testid="datasheet-viewer-title">
          Personnel Data Sheet{name ? ` — ${name}` : ''}
        </span>
      ) : <span />}
      <div className="flex items-center gap-2">
        <button onClick={doPrint} disabled={state !== 'ready'} data-testid="datasheet-print-btn"
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#0E2A47] px-3.5 py-2 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50 disabled:opacity-50">
          <Printer size={15} /> Print
        </button>
        <button onClick={doDownload} disabled={state !== 'ready'} data-testid="datasheet-download-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#5FA718] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4e8f12] disabled:opacity-50">
          <Download size={15} /> Download PDF
        </button>
        {onClose && (
          <button onClick={onClose} data-testid="datasheet-viewer-close"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
        )}
      </div>
    </div>
  );

  const body = (
    <div className={`mt-3 w-full overflow-auto rounded-lg border border-slate-200 bg-slate-100 ${heightClass}`} data-testid="datasheet-preview-scroll">
      {state === 'loading' && <div className="flex h-full items-center justify-center text-slate-400" data-testid="datasheet-viewer-loading"><Loader2 className="animate-spin" /></div>}
      {state === 'error' && (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-slate-500" data-testid="datasheet-viewer-error">
          <FileWarning className="text-amber-500" />
          <p className="text-sm">The data sheet could not be loaded. Please close this preview and try again.</p>
        </div>
      )}
      {state === 'ready' && (
        <div className="flex flex-col items-center gap-3 p-3" data-testid="datasheet-pdf-frame">
          {pages.map((src, i) => (
            <img key={i} src={src} alt={`Data sheet page ${i + 1}`} data-testid={`datasheet-page-${i + 1}`}
              className="w-full max-w-3xl rounded shadow-sm ring-1 ring-slate-200" />
          ))}
        </div>
      )}
    </div>
  );

  if (onClose) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="datasheet-viewer-modal">
        <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-2xl bg-white p-5 shadow-2xl">
          {toolbar}
          {body}
        </div>
      </div>
    );
  }
  return <div data-testid="datasheet-viewer-inline">{toolbar}{body}</div>;
};

export default DataSheetViewer;
